/* eslint-disable no-undef */
import React, { useState, useEffect, useMemo } from "react";
import { withRouter } from "react-router-dom";
import moment from "moment";

import {
  Grid,
  LinearProgress,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  Button,
  Checkbox,
  TextField,
  InputAdornment,
  ButtonGroup,
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
  MenuList,
  MenuItem,
  ListItem,
  List,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Badge,
  Menu,
} from "@material-ui/core";
import { Edit, DeleteForever, ExpandMore } from "@material-ui/icons";
import Alert from "@material-ui/lab/Alert";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import ConfirmationDialog from "../../../../../components/src/ConfirmationDialog";

import StorageProvider from "../../../../../framework/src/StorageProvider";
import {
  RoadmapTableData,
  RoadCustomizationsResponse,
  fetchRoadCustomizations,
  deleteRoadCustomization,
  ServerModel,
  RoadCustomizations,
  duplicateRoadmap,
  updateRoadmapStatus
} from "./queries";
import { useStyles } from "./styles";
import { FilterIcon, SortIcon, SearchIcon, SaveIcon, EntrollIcon, DraftIcon, SignedIcon } from "../../assets";
import ToggleButton from "../../ToggleButton.web";

type Order = "asc" | "desc";

const STATUS_CONFIG = {
  draft: { icon: DraftIcon, label: "Draft" },
  created: { icon: SaveIcon, label: "Created" },
  enrolled: { icon: EntrollIcon, label: "Enrolled" },
  signed: { icon: SignedIcon, label: "Signed" }
};

// Mirrors ALLOWED_TRANSITIONS in RoadmapStatusChange on the server, which stays
// the source of truth: anything else is rejected there with a 422.
const STATUS_TRANSITIONS: Record<string, string[]> = {
  draft: ["created", "signed"],
  created: ["signed"]
};

interface HeadCell {
  id: keyof RoadmapTableData;
  disablePadding: boolean;
  label: string;
  sortable: boolean;
  numeric: boolean;
}

const headCells: HeadCell[] = [
  {
    id: "id",
    disablePadding: false,
    sortable: false,
    numeric: false,
    label: "ID"
  },
  {
    id: "status",
    disablePadding: false,
    sortable: false,
    numeric: false,
    label: "Status"
  },
  {
    id: "name",
    disablePadding: false,
    sortable: false,
    numeric: false,
    label: "Roadmap Name"
  },
  {
    id: "created_at",
    disablePadding: false,
    sortable: false,
    numeric: false,
    label: "Date Created"
  },
  {
    id: "updated_at",
    disablePadding: false,
    sortable: false,
    numeric: false,
    label: "Date Last Modified"
  }
];

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof RoadmapTableData
  ) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  numSelected: number;
  rowCount: number;
  order: Order;
  orderBy: string;
}

const parseQueryParams = (search: string): Record<string, string> =>
  Object.fromEntries(new URLSearchParams(search));

const updateQueryParams = (
  history: any,
  location: any,
  updates: Record<string, string>
) => {
  const searchParams = new URLSearchParams(location.search);
  Object.entries(updates).forEach(([key, value]) => {
    searchParams.set(key, value);
  });
  history.replace({ ...location, search: searchParams.toString() });
};

function NewPastRoadmaps({ history, location }: any) {
  const query = parseQueryParams(location.search);
  const classes = useStyles();

  const [order, setOrder] = useState<Order>((query.order as Order) || "desc");
  const [orderBy, setOrderBy] = useState<keyof RoadmapTableData>(
    (query.orderBy as keyof RoadmapTableData) || "id"
  );
  const [page, setPage] = useState(parseInt(query.page || "0", 10));
  const [rowsPerPage, setRowsPerPage] = useState(
    parseInt(query.rowsPerPage || "10", 10)
  );
  const [markedForDelete, setMarkedForDelete] = useState<number | undefined>();
  const [token, setToken] = useState<string | null>(null);
  const queryClient = useQueryClient();

  enum RoadmapTab {
    All = "All Roadmaps",
    Archived = "Archived Roadmaps"
  }
  const [selectedMap, setSelectedMap] = useState<RoadmapTab>(RoadmapTab.All);
  const filterRef = React.useRef(null);
  const sortRef = React.useRef<HTMLButtonElement>(null);
  const [sortDropdownOpen, setSortDropdownOpen] = React.useState(false);
  const [sortSelected, setSortSelected] = React.useState<string>('id');
  const [filterDropdownOpen, setFilterDropdownOpen] = React.useState(false);
  const [filterSelected, setFilterSelected] = React.useState<string[]>([]);
  const [searchValue, setSearchValue] = React.useState<string>("");
  const [openDuplicateConfirmationDialog, setOpenDuplicateConfirmationDialog] = React.useState(false);
  const [openDeleteConfirmationDialog, setOpenDeleteConfirmationDialog] = React.useState(false);
  const [statusRowId, setStatusRowId] = React.useState<number | undefined>(undefined);
  const [statusMenu, setStatusMenu] = React.useState<{ anchor: HTMLElement; row: RoadmapTableData } | null>(null);

  function EnhancedTableHead({
    classes,
    order,
    orderBy,
    onRequestSort,
    onSelectAllClick,
    numSelected,
    rowCount
  }: EnhancedTableProps) {
    const createSortHandler = (property: keyof RoadmapTableData) => (
      event: React.MouseEvent<unknown>
    ) => {
      onRequestSort(event, property);
    };

    return (
      <TableHead>
        <TableRow>
          {selectedMap == RoadmapTab.All && <TableCell padding="checkbox">
            <Checkbox
              color="primary"
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              inputProps={{
                "aria-label": "select all roadmaps"
              }}
            />
          </TableCell>}
          {headCells.filter((headCell) =>
    selectedMap === RoadmapTab.Archived ? headCell.id !== "status" : true
  ).map(headCell => (
            <TableCell
              id={headCell.id}
              key={headCell.id}
              className={classes.tableHead}
              align={headCell.numeric ? "right" : "left"}
              padding={headCell.disablePadding ? "none" : "normal"}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              {headCell.sortable ? (
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : "asc"}
                  onClick={createSortHandler(headCell.id)}
                >
                  {headCell.label}
                  {orderBy === headCell.id ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              ) : (
                headCell.label
              )}
            </TableCell>
          ))}
          {selectedMap == RoadmapTab.All && <TableCell
            key="modify"
            className={classes.tableHead}
            align="left"
            padding="normal"
          >
            Modify
          </TableCell>}
          <TableCell
            key="delete"
            className={classes.tableHead}
            align="left"
            padding="normal"
          >
            Delete
          </TableCell>
        </TableRow>
      </TableHead>
    );
  }


  useEffect(() => {
    const fetchToken = async () => {
      const storedToken = await StorageProvider.get("authToken");
      if (storedToken) {
        setToken(storedToken);
      }
    };

    fetchToken();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (
      params.get("page") !== String(page) ||
      params.get("order") !== order ||
      params.get("orderBy") !== orderBy ||
      params.get("rowsPerPage") !== String(rowsPerPage)
    ) {
      updateQueryParams(history, location, {
        order,
        orderBy,
        page: String(page),
        rowsPerPage: String(rowsPerPage)
      });
    }
  }, [history, location, order, orderBy, page, rowsPerPage]);

  const {
    isLoading,
    isError,
    data,
    refetch,
    failureReason,
    failureCount
  } = useQuery({
    queryKey: ["roadmaps", page, rowsPerPage, order, orderBy],
    queryFn: () => fetchRoadCustomizations(token!, page + 1, rowsPerPage, searchValue, sortSelected ? sortSelected : orderBy, order, filterSelected),
    enabled: !!token,
    keepPreviousData: true
  });

  const { isPending, mutate } = useMutation({
    mutationFn: async (roadmapId: number) => {
      if (!token) {
        return;
      }
      deleteRoadCustomization(token, roadmapId, filterSelected.includes("Archived"));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["roadmaps", page, rowsPerPage, order, orderBy],
        refetchType: "active"
      });
      setTimeout(() => {
        window.location.replace(window.location.href);
      }, 20);
    },
    onSettled: () => {
      setSelected([]);
      setMarkedForDelete(undefined);
    }
  });

  const duplicateMutation = useMutation({
    mutationFn: async (roadmapIds: [number]) => {
      if (!token) {
        return;
      }
      duplicateRoadmap(token, roadmapIds);
    },
    onSuccess: () => {
      setTimeout(() => {
        window.location.replace(window.location.href);
      }, 20);
      setSelected([]);
      setOpenDuplicateConfirmationDialog(false);
    },
    onError: () => setOpenDuplicateConfirmationDialog(false)
  });

  const statusMutation = useMutation({
    mutationFn: async ({ roadmapId, status }: { roadmapId: number; status: string }) => {
      if (!token) {
        return;
      }
      return updateRoadmapStatus(token, roadmapId, status);
    },
    onSuccess: () => {
      setStatusRowId(undefined);
      refetch();
    }
  });

    useEffect(() => {
    if (token) refetch();
  }, [filterSelected, sortSelected, searchValue, token, refetch]);

  const formatPageData = (
    fetched: RoadCustomizationsResponse | undefined
  ): Array<RoadmapTableData> => {
    if (!fetched || !Array.isArray(fetched.data)) {
      return [];
    }

    return fetched.data.map((roadmap: ServerModel<RoadCustomizations>) => ({
      id: roadmap.id,
      name: roadmap.attributes?.name ?? "",
      status: roadmap.attributes?.status ?? "",
      created_at: roadmap.attributes?.created_at ?? "",
      updated_at: roadmap.attributes?.updated_at ?? ""
    }));
  };

  const editRoadmap = (row: RoadmapTableData) => {
    history.push({
      pathname: `/home/roadmaps/pastRoadmaps/${row.id}`,
      search: "?editingRoadmap=true",
      state: {
        roadmap: row,
        editingRoadmap: true
      }
    });
  };

  const formatPagination = (fetched: any): Record<string, number> => {
    if (!fetched || !fetched.pagination) {
      return { count: 0, items: 20, page: 1, pages: 1 };
    }

    const pagination = fetched.pagination;
    return {
      count: Number(pagination.count ?? 0),
      items: Number(pagination.items ?? 20),
      page: Number(pagination.page ?? 1),
      pages: Number(pagination.pages ?? 1)
    };
  };

  const formattedData = useMemo(() => formatPageData(data), [data]);
  const formattedPagination = useMemo(() => formatPagination(data), [data]);

  const formatDate = (inputDate: Date | number | string): string => {
    const date = moment(inputDate);
    const now = moment();

    if (now.diff(date, "days") < 5) {
      return date.fromNow();
    }

    return date.format("MM/DD/YYYY");
  };

  const handleRequestSort = (
    _: React.MouseEvent<unknown>,
    property: keyof RoadmapTableData
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const [selected, setSelected] = React.useState<readonly number[]>([]);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = formattedData.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event: React.MouseEvent<unknown>, id: number) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: readonly number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handleSortSelect = (value: string) => {
    if (sortSelected === value) {
      setSortSelected('id');
    } else {
      setSortSelected(value);
    }
  };

  const handleFilterSelect = (value: string) => {
    const currentIndex = filterSelected.indexOf(value);
    const newChecked = [...filterSelected];
    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setFilterSelected(newChecked);
  };

  const renderStatusLabel = (status: string) => {
    const config = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG];

    if (!config) {
      return <span>{status}</span>;
    }

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
        <span>{config.label}</span>
        <img className={classes.statusImg} src={config.icon} alt={config.label} />
      </div>
    );
  };

  const changeStatus = (row: RoadmapTableData, nextStatus: string) => {
    if (statusMutation.isPending || nextStatus === row.status) {
      return;
    }

    setStatusRowId(row.id);
    statusMutation.mutate({ roadmapId: row.id, status: nextStatus });
  };

  const getStatusCell = (row: RoadmapTableData) => {
    const nextStatuses = STATUS_TRANSITIONS[row.status] ?? [];

    if (nextStatuses.length === 0) {
      return <div className={classes.statusBox}>{renderStatusLabel(row.status)}</div>;
    }

    return (
      <button
        type="button"
        className={`${classes.statusBox} ${classes.statusButton}`}
        disabled={statusMutation.isPending}
        aria-haspopup="true"
        aria-label={`Status of roadmap ${row.id}`}
        onClick={(event) => setStatusMenu({ anchor: event.currentTarget, row })}
      >
        {renderStatusLabel(row.status)}
        <ExpandMore className={classes.statusCaret} />
      </button>
    );
  };

  const statusMenuOptions = statusMenu
    ? STATUS_TRANSITIONS[statusMenu.row.status] ?? []
    : [];

  return (
    <div className={classes.root}>
      <Snackbar
        open={Boolean(duplicateMutation?.isError)}
        autoHideDuration={1200}
        onClose={() => {
          setSelected([]);
          duplicateMutation.reset();
        }}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error" elevation={6} variant="filled">
          <Typography variant="h3">
            {`Road map duplication of Road map failed!`}
          </Typography>
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(duplicateMutation.isSuccess)}
        autoHideDuration={1200}
        onClose={() => {
          setSelected([]);
          duplicateMutation.reset();
        }}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success" elevation={6} variant="filled">
          <Typography variant="h3">
            {`Road map duplication created successfully!`}
          </Typography>
        </Alert>
      </Snackbar>
      <Menu
        anchorEl={statusMenu?.anchor ?? null}
        open={Boolean(statusMenu)}
        onClose={() => setStatusMenu(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        {statusMenuOptions.map((status) => (
          <MenuItem
            key={status}
            onClick={() => {
              const row = statusMenu?.row;
              setStatusMenu(null);
              if (row) {
                changeStatus(row, status);
              }
            }}
          >
            {renderStatusLabel(status)}
          </MenuItem>
        ))}
      </Menu>
      <Snackbar
        open={Boolean(statusMutation.isError)}
        autoHideDuration={4000}
        onClose={() => statusMutation.reset()}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error" elevation={6} variant="filled">
          <Typography variant="h3">
            {`Status update failed${statusRowId ? ` for roadmap ID:${statusRowId}` : ""}!`}
          </Typography>
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(statusMutation.isSuccess)}
        autoHideDuration={1200}
        onClose={() => statusMutation.reset()}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success" elevation={6} variant="filled">
          <Typography variant="h3">Status updated successfully!</Typography>
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(isError)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error" elevation={6} variant="filled">
          <Typography variant="h3">An error occurred!</Typography>
          <Typography>
            {failureCount != null ? `${failureCount} attempts made` : ""}
          </Typography>
          <Typography>
            {failureReason != null ? `Details: ${String(failureReason)}` : ""}
          </Typography>
          <Button
            color="inherit"
            size="medium"
            variant="text"
            onClick={_ => refetch()}
          >
            Retry
          </Button>
        </Alert>
      </Snackbar>
      <ConfirmationDialog
        title="Roadmap deletion"
        questionText={`Are you sure you want to delete roadmap? ID:${markedForDelete}`}
        isOpen={Boolean(markedForDelete)}
        isLoading={isPending}
        handleClose={() => setMarkedForDelete(undefined)}
        onConfirm={() => mutate(markedForDelete!)}
      />
      <Grid container justifyContent="center" alignItems="center">
        <Grid item xs={12} md={12} style={{ marginBottom: "2.5rem", marginTop: "-0.9rem" }}>
          <Typography variant="h1" className={classes.title}>
            Access Past Roadmaps
          </Typography>
          <Typography className={classes.subTitle}>
            Here is a list of your saved roadmaps. You can view or edit them by
            clicking on the edit icon below.
          </Typography>
        </Grid>
      </Grid>
      <ToggleButton
        list={[RoadmapTab.All, RoadmapTab.Archived]}
        selectedData={selectedMap}
        onSelecteData={(value: React.SetStateAction<string>) => {
          if (value === RoadmapTab.Archived) {
            handleFilterSelect("Archived");
          } else {
            const currentIndex = filterSelected.indexOf("Archived");
            const newChecked = [...filterSelected];
            if (currentIndex !== -1) {
              newChecked.splice(currentIndex, 1);
            }
            setFilterSelected(newChecked);
          }
          setSelectedMap(value as RoadmapTab);
        }
        }
      />
      <Grid container justifyContent="flex-start" alignItems="center" style={{ marginBottom: "1rem" }}>
        <Grid style={{ marginRight: '5px' }} item xs={1}>
          <Button
            variant={"outlined"}
            ref={sortRef}
            aria-controls={sortDropdownOpen ? 'menu-list-grow' : undefined}
            aria-haspopup="true"
            onClick={() => setSortDropdownOpen((prev) => !prev)}
            type="button"
            fullWidth
            id="filter-button"
            startIcon={<img style={{ width: "15px" }} src={SortIcon} />}
          >Sort</Button>
          <Popper style={{ zIndex: 99 }} open={sortDropdownOpen} anchorEl={sortRef.current} role={undefined} transition disablePortal>
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={() => setSortDropdownOpen(false)}>
                    <List component="nav" aria-label="sorting options" style={{ padding: '8px' }}>
                      {
                        [{ name: 'A-Z', val: "name" }, { name: "created date", val: "created_at" }, { name: "modified date", val: "updated_at" }].map((value) => {
                          const labelId = `checkbox-list-label-${value.name}`;
                          return (
                            <ListItem style={{ paddingTop: "0px", paddingBottom: "0px" }} key={value.name} button onClick={() => handleSortSelect(value.val)}>
                              <ListItemIcon style={{ paddingTop: "0px", paddingBottom: "0px", minWidth: "20px" }}>
                                <Checkbox
                                  edge="start"
                                  checked={sortSelected === value.val}
                                  tabIndex={-1}
                                  disableRipple
                                  inputProps={{ 'aria-labelledby': labelId }}
                                />
                              </ListItemIcon>
                              <ListItemText primary={value.name.toUpperCase()} />
                            </ListItem>
                          );
                        })
                      }
                    </List>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Grid>
        <Grid style={{ marginRight: '5px' }} item xs={1}>
          <Button
            variant={"outlined"}
            fullWidth
            ref={filterRef}
            aria-controls={filterDropdownOpen ? 'menu-list-grow' : undefined}
            aria-haspopup="true"
            onClick={() => setFilterDropdownOpen((prev) => !prev)}
            type="button"
            id="filter-button"
            startIcon={<img style={{ width: "15px" }} src={FilterIcon} />}
          >Filters</Button>
          <Popper style={{ zIndex: 99 }} open={filterDropdownOpen} anchorEl={filterRef.current} role={undefined} transition disablePortal>
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={() => setFilterDropdownOpen(false)}>
                    <List component="nav" aria-label="filter options" style={{ padding: '8px' }}>
                      {
                        ["Draft", "Created", "Enrolled", "Signed"].map((value) => {
                          const labelId = `checkbox-list-label-${value}`;
                          const getColor = (status: string) => {
                            switch (status) {
                              case "Draft":
                                return "#EDA01C";
                              case "Created":
                                return "#0075FB";
                              case "Enrolled":
                                return "#74C283";
                              case "Signed":
                                return "#0E8A8A";
                              default:
                                return "#000000";
                            }
                          };
                          return (
                            <ListItem style={{ paddingTop: "0px", paddingBottom: "0px" }} key={value} button onClick={() => handleFilterSelect(value)}>
                              <ListItemIcon style={{ paddingTop: "0px", paddingBottom: "0px", minWidth: "20px" }}>
                                <Checkbox
                                  edge="start"
                                  checked={filterSelected.indexOf(value) !== -1}
                                  tabIndex={-1}
                                  disableRipple
                                  inputProps={{ 'aria-labelledby': labelId }}
                                />
                              </ListItemIcon>
                              <ListItemText primary={value.toUpperCase()} />
                              <ListItemSecondaryAction>

                                <div style={{ minWidth: "8px", height: "8px", borderRadius: "50%", background: getColor(value) }}></div>
                              </ListItemSecondaryAction>
                            </ListItem>
                          );
                        })
                      }
                    </List>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Grid>
        {selectedMap == RoadmapTab.All && <Grid style={{ marginRight: '5px' }} item xs={2}>
          <ConfirmationDialog
            title="Roadmap duplication"
            questionText={`Are you sure you want to duplicate selected roadmaps? IDs: ${selected.join(", ")}`}
            isOpen={Boolean(openDuplicateConfirmationDialog)}
            isLoading={duplicateMutation.isPending}
            handleClose={() => setOpenDuplicateConfirmationDialog(false)}
            onConfirm={() => duplicateMutation.mutate(selected as [number])}
          />
          <Button
            color="primary"
            fullWidth
            variant={"contained"}
            type="button"
            id="duplicate-button"
            onClick={() => setOpenDuplicateConfirmationDialog(true)}
            disabled={selected.length == 0}
          >duplicate All</Button>
        </Grid>}
        {/* <Grid item xs={2}>
          <ConfirmationDialog
            title="Roadmap deletion All"
            questionText={`Are you sure you want to delete selected roadmaps? IDs: ${selected.join(", ")}`}
            isOpen={Boolean(openDeleteConfirmationDialog)}
            isLoading={isPending}
            handleClose={() => setOpenDeleteConfirmationDialog(false)}
            onConfirm={() => mutate(selected[0])}
          />
          <Button
            color="primary"
            fullWidth
            variant={"contained"}
            type="button"
            id="duplicate-button"
            onClick={() => setOpenDeleteConfirmationDialog(true)}
            disabled={selected.length == 0}
          >Delete All</Button>
        </Grid> */}
        <Grid item xs={5} md={4} style={{ marginLeft: 'auto' }}>
          <TextField
            variant="outlined"
            placeholder="Search"
            fullWidth
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment
                  position="start"
                  style={{
                    paddingRight: 0,
                  }}

                >
                  <img style={{ width: "15px" }} src={SearchIcon} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>
      <Grid container justifyContent="center" alignItems="center">
        <Grid item xs={12} md={12}>
          <TableContainer>
            <Table
              className={classes.table}
              aria-labelledby="tableTitle"
              size="medium"
              aria-label="enhanced table"
            >
              <EnhancedTableHead
                classes={classes}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                numSelected={selected.length}
                rowCount={formattedData.length}
                onSelectAllClick={handleSelectAllClick}
              />
              {isLoading && (
                <TableRow>
                  <TableCell
                    colSpan={12}
                    component="th"
                    scope="row"
                    align="center"
                  >
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && !isError && formattedData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    {" "}
                    No past Roadmaps to show.
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && !isError && formattedData && (
                <TableBody>
                  {formattedData.map((row, index) => {
                    const isItemSelected = selected.includes(row.id);
                    const labelId = `enhanced-table-checkbox-${index}`;
                    return (
                      <TableRow hover
                        tabIndex={-1}
                        key={row.id}
                        selected={isItemSelected}
                      // sx={{ cursor: 'pointer' }}
                      >
                        {selectedMap == RoadmapTab.All && <TableCell padding="checkbox">
                          <Checkbox
                            color="primary"
                             onClick={(event) => handleClick(event, row.id)}
                            checked={isItemSelected}
                            inputProps={{
                              'aria-labelledby': labelId,
                            }}
                          />
                        </TableCell>}
                        <TableCell id={labelId} align="left">{row.id}</TableCell>
                        
                        {selectedMap !== RoadmapTab.Archived && (
                          <TableCell
                            component="th"
                            scope="row"
                            align="left"
                            style={{ fontWeight: "bold" }}
                          >
                            {getStatusCell(row)}
                          </TableCell>
                        )}

                        <TableCell
                          component="th"
                          scope="row"
                          align="left"
                          style={{ fontWeight: "bold" }}
                        >
                          {row.name}
                        </TableCell>
                        <TableCell align="left">
                          {formatDate(row.created_at)}
                        </TableCell>
                        <TableCell align="left">
                          {formatDate(row.updated_at)}
                        </TableCell>
                        {selectedMap == RoadmapTab.All && <TableCell align="left">
                          <Edit
                            fontSize="large"
                            style={{ cursor: "pointer" }}
                            onClick={() => editRoadmap(row)}
                          />
                        </TableCell>}
                        <TableCell align="left">
                          <DeleteForever
                            fontSize="large"
                            style={{ cursor: "pointer" }}
                            onClick={() => setMarkedForDelete(row.id)}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              )}
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={formattedPagination.count}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage: number) => setPage(newPage)}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Grid>
      </Grid>
    </div>
  );
}

export default withRouter(NewPastRoadmaps);
